import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    Badge,
    Flex,
    HStack,
    Stack,
    Textarea,
    useClipboard,
} from '@chakra-ui/react';
import { toast } from 'react-toastify';
import {
    Button,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
    Tooltip,
} from '@/components/v4';
import { FOOTER_BUTTON_WIDTH, fixedModalHeight } from '../constants';
import {
    formatPastedNumbers,
    matchPastedNumbers,
    parsePastedNumbers,
} from '../utils/pasteList';

const toNormalisedText = (text) => {
    const numbers = parsePastedNumbers(text);
    return numbers.length ? `${formatPastedNumbers(numbers)}\n` : text;
};

/**
 * Paste numbers from Excel, CSV or an email to select them. Matching is exact
 * (digits including country code), and the list is remembered for the next
 * open so not-found numbers can be fixed and re-checked. While the list is
 * filtering All Numbers, the dialog also offers to clear that filter.
 */
const PasteListModal = ({
    isOpen,
    onClose,
    rows,
    rememberedText,
    onSelect,
    isFiltering,
    onClearFilter,
}) => {
    const { darkMode } = useSelector((state) => state.settings);
    const [text, setText] = useState('');

    useEffect(() => {
        if (isOpen) setText(rememberedText);
    }, [isOpen, rememberedText]);

    const numbers = useMemo(() => parsePastedNumbers(text), [text]);
    const { found, notFound, matchedIds } = useMemo(
        () => matchPastedNumbers(numbers, rows),
        [numbers, rows],
    );
    const foundSet = useMemo(() => new Set(found), [found]);
    // Greyed out numbers still match (and filter) but can't be ticked
    const unselectableReasons = useMemo(
        () =>
            new Map(
                rows
                    .filter((row) => row.unselectableReason)
                    .map((row) => [row.digits, row.unselectableReason]),
            ),
        [rows],
    );
    const unselectableCount = found.filter((digits) =>
        unselectableReasons.has(digits),
    ).length;
    const { onCopy } = useClipboard(formatPastedNumbers(notFound));

    const handlePaste = (event) => {
        event.preventDefault();
        const pasted = event.clipboardData.getData('text');
        setText(toNormalisedText(`${text}\n${pasted}`));
    };

    const handleCopyNotFound = () => {
        onCopy();
        toast.success(
            `Copied ${notFound.length} not-found ${notFound.length === 1 ? 'number' : 'numbers'}`,
        );
    };

    const handleSelect = () =>
        onSelect({ text, found, matchedIds, total: numbers.length });

    const foundColors = darkMode
        ? { bg: 'rgba(154, 230, 180, 0.16)', color: 'green.200' }
        : { bg: 'green.50', color: 'green.800' };
    const notFoundColors = darkMode
        ? { bg: 'rgba(254, 178, 178, 0.16)', color: 'red.200' }
        : { bg: 'red.50', color: 'red.800' };
    const unselectableColors = darkMode
        ? { bg: 'whiteAlpha.100', color: 'gray.300' }
        : { bg: 'gray.100', color: 'gray.600' };

    const getMatch = (digits) => {
        if (!foundSet.has(digits)) {
            return {
                label: 'Not found',
                scheme: 'red',
                colors: notFoundColors,
            };
        }
        const reason = unselectableReasons.get(digits);
        return reason
            ? {
                  label: "Can't select",
                  scheme: 'gray',
                  colors: unselectableColors,
                  reason,
              }
            : { label: 'Found', scheme: 'green', colors: foundColors };
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="2xl"
            closeOnOverlayClick
            // Focus going back to Paste List would open its tooltip, which then
            // stays over the table
            returnFocusOnClose={false}>
            <ModalOverlay />
            <ModalContent
                bg={darkMode ? 'gray.800' : 'white'}
                sx={fixedModalHeight('640px')}>
                <ModalHeader color={darkMode ? 'white' : 'gray.900'}>
                    Paste List
                </ModalHeader>
                <ModalCloseButton />
                {/* The match list fills the space left and scrolls */}
                <ModalBody display="flex" flexDirection="column" minH={0}>
                    <Stack spacing={3} flex="1" minH={0}>
                        <Text fontSize="sm" color="gray.500">
                            Paste numbers from Excel, CSV, or emails to select
                            them automatically.
                        </Text>
                        <Textarea
                            rows={7}
                            resize="none"
                            flexShrink={0}
                            autoFocus
                            aria-label="Numbers to match"
                            placeholder={
                                '+441234567890\n+81123456789\n+11234567890\n+491234567890'
                            }
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            onPaste={handlePaste}
                            onBlur={() => setText(toNormalisedText(text))}
                            fontFamily="mono"
                            fontSize="sm"
                            bg={darkMode ? 'dark.bgDark' : null}
                            borderColor={
                                darkMode ? 'dark.tcap-borderGray' : null
                            }
                        />
                        <Flex
                            justify="space-between"
                            align="center"
                            fontSize="xs"
                            color="gray.500">
                            <Text fontSize="xs" m={0}>
                                Extracts phone digits automatically
                            </Text>
                            <HStack spacing={3}>
                                <Text fontSize="xs" m={0} aria-live="polite">
                                    {numbers.length
                                        ? `${found.length} found${unselectableCount ? ` (${unselectableCount} can't be selected)` : ''} · ${notFound.length} not found`
                                        : '0 numbers detected'}
                                </Text>
                                {notFound.length > 0 && (
                                    <Button
                                        variant="link"
                                        size="xs"
                                        onClick={handleCopyNotFound}>
                                        Copy not-found numbers
                                    </Button>
                                )}
                            </HStack>
                        </Flex>
                        {numbers.length > 0 && (
                            <Stack
                                spacing={1}
                                flex="1"
                                minH={0}
                                overflowY="auto"
                                aria-label="Matched numbers">
                                {numbers.map((digits) => {
                                    const match = getMatch(digits);
                                    return (
                                        <Flex
                                            key={digits}
                                            justify="space-between"
                                            align="center"
                                            px={3}
                                            py={1}
                                            borderRadius="md"
                                            bg={match.colors.bg}>
                                            <Text
                                                fontSize="sm"
                                                fontFamily="mono"
                                                color={match.colors.color}>
                                                +{digits}
                                            </Text>
                                            <Tooltip
                                                label={match.reason}
                                                isDisabled={!match.reason}
                                                hasArrow>
                                                <Badge
                                                    textTransform="none"
                                                    colorScheme={match.scheme}>
                                                    {match.label}
                                                </Badge>
                                            </Tooltip>
                                        </Flex>
                                    );
                                })}
                            </Stack>
                        )}
                    </Stack>
                </ModalBody>
                <ModalFooter justifyContent="flex-start" gap={3}>
                    <Button
                        flexShrink={0}
                        isDisabled={found.length === 0}
                        onClick={handleSelect}>
                        Select Matching Numbers
                    </Button>
                    <Button
                        variant="outline"
                        flexShrink={0}
                        minW={FOOTER_BUTTON_WIDTH}
                        onClick={onClose}>
                        Cancel
                    </Button>
                    {isFiltering && (
                        <Button
                            variant="link"
                            size="sm"
                            ml="auto"
                            onClick={onClearFilter}>
                            Clear list filter
                        </Button>
                    )}
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default PasteListModal;
